import { AdminTableContainer, Badge, Button, cn, EmptyState } from "@simplicity/ui";
import { RunStatusBadge } from "./OnsiteBadges";
import { describeCounts, formatDateTime, planOf } from "./onsiteModel";
import { describeUsers, runActionLabel, runUsersOf } from "./sftpUsersModel";
import type { OnsiteRun } from "./types";

function ErrorBlock({ message }: { message: string | null }) {
  if (!message) return <span className="col-muted">—</span>;
  return (
    <pre
      className="max-h-40 max-w-md overflow-auto whitespace-pre rounded bg-destructive/10 p-2 text-xs text-destructive"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region must be keyboard-reachable (axe scrollable-region-focusable)
      tabIndex={0}
      aria-label="Error message"
    >
      {message}
    </pre>
  );
}

function UsersCell({ run }: { run: OnsiteRun }) {
  const users = runUsersOf(run);
  if (!users) return <span className="col-muted">—</span>;
  return <span>{describeUsers(users)}</span>;
}

function ChangesCell({ run, approvable }: { run: OnsiteRun; approvable: boolean }) {
  const plan = planOf(run);
  if (!plan) return <UsersCell run={run} />;
  const destructive = plan.replace + plan.delete > 0;
  return (
    <div className="space-y-1">
      <span className={cn(destructive && "font-semibold text-destructive")}>
        {describeCounts(plan)}
      </span>
      {approvable && (
        <div>
          <Badge variant="warning">awaiting approval</Badge>
        </div>
      )}
    </div>
  );
}

function RunRow({
  run,
  selected,
  approvable,
  onSelect,
}: {
  run: OnsiteRun;
  selected: boolean;
  approvable: boolean;
  onSelect: (id: number) => void;
}) {
  return (
    <tr className={cn(selected && "bg-muted")} aria-current={selected ? "true" : undefined}>
      <td>
        <Button variant="link" className="h-auto px-0" onClick={() => onSelect(run.id)}>
          #{run.id}
        </Button>
      </td>
      <td>{runActionLabel(run)}</td>
      <td>
        <RunStatusBadge status={run.status} />
      </td>
      <td className="col-muted">
        {formatDateTime(run.triggeredAt)}
        {run.triggeredByEmail && <div>{run.triggeredByEmail}</div>}
      </td>
      <td className="col-muted">{formatDateTime(run.completedAt)}</td>
      <td className="col-muted">{run.approvedByEmail ?? "—"}</td>
      <td>
        <ChangesCell run={run} approvable={approvable} />
      </td>
      <td>
        <ErrorBlock message={run.errorMessage} />
      </td>
    </tr>
  );
}

export function OnsiteRunsTable({
  runs,
  selectedRunId,
  approvableRunId,
  onSelect,
}: {
  runs: OnsiteRun[];
  selectedRunId: number | null;
  approvableRunId: number | null;
  onSelect: (id: number) => void;
}) {
  if (runs.length === 0) {
    return (
      <div className="admin-table-container">
        <EmptyState title="No runs yet" description="Request a plan to start." />
      </div>
    );
  }
  return (
    <AdminTableContainer aria-label="Onsite runs">
      <table className="admin-table">
        <thead>
          <tr>
            <th>Run</th>
            <th>Action</th>
            <th>Status</th>
            <th>Triggered</th>
            <th>Completed</th>
            <th>Approved by</th>
            <th>Changes</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <RunRow
              key={run.id}
              run={run}
              selected={run.id === selectedRunId}
              approvable={run.id === approvableRunId}
              onSelect={onSelect}
            />
          ))}
        </tbody>
      </table>
    </AdminTableContainer>
  );
}
